/**
 * Enhanced Teacher dashboard using the new component system
 * Demonstrates teacher class selection with enhanced layout and functionality
 */

import React, { useEffect, useState } from 'react';
import {
  FiArrowRight,
  FiBarChart,
  FiBookOpen,
  FiCalendar,
  FiSearch,
  FiUsers,
} from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';

// New component system imports
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  Stack,
  useColorModeValue,
  Button,
  HStack,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { AdminLayout, Breadcrumb } from '@grcs/components';
import { SEO } from '@grcs/components/SEO';

// Existing hooks and types
import {
  useClassOperations,
  useSemesterOperations,
} from '@grcs/hook/useDatabase';
import { useLocalStorage } from '@grcs/hook/useLocalStorage';
import { ClassProfile } from '@grcs/types/DataTypes';
import { isNotUndefined } from '@grcs/utils/isNotUndefined';

export interface IRouteProps {
  from: string;
}

interface TeacherStats {
  totalClasses: number;
  totalStudents: number;
  currentSemester: string;
  activeClasses: number;
}

export const TeacherEnhanced: React.FC<IRouteProps> = ({ from }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const surface = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.600');
  const muted = useColorModeValue('gray.600', 'gray.300');
  const hoverSurface = useColorModeValue('blue.50', 'gray.700');
  const accent = useColorModeValue('blue.600', 'blue.300');
  const [{ user_token, full_name }] = useLocalStorage('user', {});
  const classOps = useClassOperations();
  const semesterOps = useSemesterOperations();

  const [currentYear, setCurrentYear] = useState('');
  const [allClasses, setAllClasses] = useState<ClassProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState<TeacherStats>({
    totalClasses: 0,
    totalStudents: 0,
    currentSemester: '',
    activeClasses: 0,
  });

  // Load current semester year with comprehensive error handling
  useEffect(() => {
    const loadCurrentYear = async () => {
      try {
        console.log('🗓️ Loading current semester for Teacher dashboard...');
        const { data, error: semesterError } =
          await semesterOps.getCurrentSemesterYear();

        if (semesterError) {
          console.error('❌ Error fetching current semester:', semesterError);
          setError(
            'Failed to load semester information. Please try refreshing the page or contact your administrator.',
          );
          setIsLoading(false);
          return;
        }

        if (data && data.length > 0) {
          const year = data[0].semester_year;
          console.log(`✅ Active semester found: ${year}`);
          setCurrentYear(year);
          setStats((prev) => ({
            ...prev,
            currentSemester: year,
          }));
          setError(''); // Clear any previous errors
        } else {
          console.warn('⚠️ No active semester found');
          setError(
            'No active semester is currently set. Please contact your administrator to create and activate a semester. You cannot access classes until a semester is active.',
          );
          setIsLoading(false);
          // Don't load class data if no active semester
          return;
        }
      } catch (err) {
        console.error('💥 Unexpected error loading current year:', err);
        setError(
          'An unexpected error occurred while loading semester information. Please try refreshing the page or contact your administrator.',
        );
        setIsLoading(false);
      }
    };

    if (!currentYear) {
      loadCurrentYear();
    }
  }, [currentYear, semesterOps]);

  // Load teacher's classes - only when we have valid semester and user
  useEffect(() => {
    const loadClasses = async () => {
      // Don't load classes if no user token, no current year, or there are semester errors
      if (!user_token) {
        console.log('⏭️ Skipping class loading - no user token');
        return;
      }

      if (!currentYear) return;

      if (error) {
        console.log(
          '⏭️ Skipping class loading - no active semester or error present',
        );
        setAllClasses([]);
        setStats((prev) => ({
          ...prev,
          totalClasses: 0,
          totalStudents: 0,
          activeClasses: 0,
        }));
        setIsLoading(false);
        return;
      }

      console.log(
        `👨‍🏫 Loading classes for teacher: ${user_token} in semester: ${currentYear}`,
      );
      try {
        setIsLoading(true);
        const { data, error: classError } =
          await classOps.getClassesByTeacherId(user_token);
        if (classError) throw classError;
        const classes = (data || []).filter(isNotUndefined);
        setAllClasses(classes);

        // Calculate stats
        const currentClasses = classes.filter(
          (c) => c.semester_year === currentYear,
        );
        const totalStudents = currentClasses.reduce(
          (sum, c) => sum + (c.number_of_students || 0),
          0,
        );

        setStats((prev) => ({
          ...prev,
          totalClasses: classes.length,
          activeClasses: currentClasses.length,
          totalStudents,
        }));
      } catch (error) {
        console.error('Error loading classes:', error);
        setError('Your classes could not be loaded. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadClasses();
  }, [classOps, user_token, currentYear, error]);

  // Filter current classes
  const currentClasses = allClasses.filter(
    (c) => c.semester_year === currentYear,
  );

  const filteredClasses = currentClasses
    .filter((classItem) =>
      `${classItem.class_name} ${classItem.grade}`
        .toLowerCase()
        .includes(searchQuery.trim().toLowerCase()),
    )
    .sort((a, b) =>
      a.class_name.localeCompare(b.class_name, undefined, { numeric: true }),
    );
  const isAnalytics = from === 'analytics';
  const classRoute = isAnalytics
    ? 'analytics'
    : from === 'homework'
    ? 'homeworks'
    : 'home';
  const pageTitle = isAnalytics ? 'Class analytics' : 'Teacher home';

  return (
    <AdminLayout padding={3}>
      <SEO
        title={isAnalytics ? 'Class Analytics' : 'Teacher Dashboard'}
        description="Manage your classes"
        keywords="teacher dashboard, class management, homework assignments, Grand River Chinese School"
      />
      <VStack spacing={4} align="stretch">
        <Box>
          <Breadcrumb
            items={
              isAnalytics
                ? [
                    { label: 'Teacher', href: '/teacher' },
                    { label: 'Class analytics', isCurrent: true },
                  ]
                : [{ label: 'Teacher home', isCurrent: true }]
            }
          />
          <Flex
            justify="space-between"
            align="center"
            gap={3}
            wrap="wrap"
            mt={3}
          >
            <Box>
              <Heading size="md">
                {isAnalytics
                  ? pageTitle
                  : `Welcome${full_name ? `, ${full_name}` : ''}`}
              </Heading>
            </Box>
            <Button
              as={RouterLink}
              to="/teacher/student-analytics"
              size="sm"
              colorScheme="blue"
              leftIcon={<FiBarChart />}
            >
              Student analytics
            </Button>
          </Flex>
        </Box>

        {error ? (
          <Alert status="error" borderRadius="lg" alignItems="flex-start">
            <AlertIcon />
            <Box flex="1">
              <Text fontWeight="600" fontSize="sm">
                Unable to load your classes
              </Text>
              <Text fontSize="sm" mt={1}>
                {error}
              </Text>
              <Button
                size="sm"
                variant="outline"
                mt={3}
                onClick={() => window.location.reload()}
              >
                Try again
              </Button>
            </Box>
          </Alert>
        ) : (
          <>
            <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3}>
              {[
                {
                  label: 'Current classes',
                  value: stats.activeClasses,
                  icon: FiBookOpen,
                },
                {
                  label: 'Students',
                  value: stats.totalStudents,
                  icon: FiUsers,
                },
                {
                  label: 'Classes taught',
                  value: stats.totalClasses,
                  icon: FiCalendar,
                },
              ].map(({ label, value, icon: Icon }) => (
                <Box
                  key={label}
                  bg={surface}
                  borderWidth="1px"
                  borderColor={border}
                  borderRadius="lg"
                  p={4}
                >
                  <HStack justify="space-between" color={muted} mb={2}>
                    <Text fontSize="sm">{label}</Text>
                    <Icon aria-hidden="true" />
                  </HStack>
                  <Skeleton isLoaded={!isLoading}>
                    <Text fontSize="2xl" fontWeight="bold" lineHeight="short">
                      {value}
                    </Text>
                  </Skeleton>
                </Box>
              ))}
            </SimpleGrid>

            <Box
              bg={surface}
              borderWidth="1px"
              borderColor={border}
              borderRadius="lg"
              p={{ base: 3, md: 4 }}
            >
              <Stack
                direction={{ base: 'column', md: 'row' }}
                justify="space-between"
                spacing={3}
                mb={3}
              >
                <Box>
                  <HStack spacing={3} flexWrap="wrap">
                    <Heading size="sm">Your classes</Heading>
                    {currentYear && (
                      <Badge colorScheme="blue" borderRadius="full" px={2}>
                        {currentYear}
                      </Badge>
                    )}
                  </HStack>
                </Box>
                <InputGroup size="sm" maxW={{ md: '300px' }}>
                  <InputLeftElement pointerEvents="none">
                    <FiSearch />
                  </InputLeftElement>
                  <Input
                    aria-label="Search classes"
                    placeholder="Search class or grade"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    borderRadius="md"
                  />
                </InputGroup>
              </Stack>
              <Text fontSize="xs" color={muted} mb={3} role="status">
                {isLoading
                  ? 'Loading your classes…'
                  : `${filteredClasses.length} of ${currentClasses.length} classes`}
              </Text>

              {isLoading ? (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                  {[0, 1, 2].map((index) => (
                    <Skeleton key={index} height="164px" borderRadius="lg" />
                  ))}
                </SimpleGrid>
              ) : filteredClasses.length === 0 ? (
                <VStack spacing={2} py={10} textAlign="center" color={muted}>
                  <FiBookOpen size={28} aria-hidden="true" />
                  <Text fontWeight="600">
                    {searchQuery.trim()
                      ? 'No matching classes'
                      : 'No classes this semester'}
                  </Text>
                  <Text fontSize="sm">
                    {searchQuery.trim()
                      ? 'Try another class name or grade.'
                      : `You have no assigned classes for ${currentYear}. Contact your administrator if a class is missing.`}
                  </Text>
                  {searchQuery && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSearchQuery('')}
                    >
                      Clear search
                    </Button>
                  )}
                </VStack>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                  {filteredClasses.map((classItem) => (
                    <Box
                      as={RouterLink}
                      to={`/teacher/${currentYear}/class/${classItem.class_token}/${classRoute}`}
                      key={classItem.class_token}
                      borderWidth="1px"
                      borderColor={border}
                      borderRadius="lg"
                      p={4}
                      display="flex"
                      flexDirection="column"
                      gap={3}
                      minW={0}
                      _hover={{
                        borderColor: accent,
                        bg: hoverSurface,
                        textDecoration: 'none',
                      }}
                      _focusVisible={{
                        outline: '2px solid',
                        outlineColor: accent,
                        outlineOffset: '2px',
                      }}
                    >
                      <HStack justify="space-between" align="start">
                        <Heading size="sm" overflowWrap="anywhere">
                          {classItem.class_name}
                        </Heading>
                        <Box color={accent} flexShrink={0}>
                          <FiBookOpen aria-hidden="true" />
                        </Box>
                      </HStack>
                      <HStack spacing={3} flexWrap="wrap">
                        <Badge colorScheme="blue" variant="subtle">
                          Grade {classItem.grade}
                        </Badge>
                        <HStack spacing={1} color={muted}>
                          <FiUsers aria-hidden="true" />
                          <Text fontSize="sm">
                            {classItem.number_of_students || 0}{' '}
                            {classItem.number_of_students === 1
                              ? 'student'
                              : 'students'}
                          </Text>
                        </HStack>
                      </HStack>
                      <HStack
                        justify="space-between"
                        color={accent}
                        mt="auto"
                        pt={2}
                        borderTopWidth="1px"
                        borderColor={border}
                      >
                        <Text fontSize="sm" fontWeight="600">
                          {isAnalytics
                            ? 'Summary'
                            : from === 'homework'
                            ? 'Assign homework'
                            : 'Dashboard'}
                        </Text>
                        <FiArrowRight aria-hidden="true" />
                      </HStack>
                    </Box>
                  ))}
                </SimpleGrid>
              )}
            </Box>
          </>
        )}
      </VStack>
    </AdminLayout>
  );
};
